import { createContext, useContext, useEffect, useState } from "react";
import {
  CurrentQuestionRenderer,
  NextButton,
  Question,
  QuestionUserType,
  UserInfoProvider,
  useUserInfo,
} from "@/components/shared/Questionnaire";
import {
  Box,
  Button,
  chakra,
  Flex,
  FormControl,
  FormErrorMessage,
  Input,
  Spacer,
  Text,
} from "@chakra-ui/react";
import { createValueList } from "@/utils/ui/select-helper";
import { Select } from "chakra-react-select";
import { useAPI } from "@/contexts/APIContext";
import { useUserData } from "@/contexts/UserDataContext";
import current from "@/utils/data/constants";
import { useRouter } from "next/router";
import { HSCreateAccount } from "@/components/user/CreateAccount";
import { useForm } from "react-hook-form";
import { ChevronRightIcon } from "@chakra-ui/icons";
import { hasTenDigits, isValidEmail, isValidPhoneNumber } from "@/utils/string";

const CollegeAdvisorFormContext = createContext();

const useCollegeAdvisorForm = () => useContext(CollegeAdvisorFormContext);

function Intro() {
  const { context } = useCollegeAdvisorForm();
  return (
    <Box textAlign="left" sx={{ "> p": { fontSize: "lg" } }} flex={2}>
      <chakra.h1
        mt={4}
        fontSize={context === "page" ? { base: "4xl", md: "6xl" } : "3xl"}
        textStyle="pageHeader"
        textAlign={"center"}
      >
        🤝 Get Free Expert Help
      </chakra.h1>
      <Text
        fontWeight={"bold"}
        textAlign={"center"}
        fontFamily={"monospace"}
        fontSize={"xl"}
        color={"gray.500"}
        fontStyle={"italic"}
      >
        ⏰ Estimated Time: 1 minute
      </Text>
      <Text mt={4}>
        Do you hope to attend an elite college or university? 91% of the students we help are
        accepted to their top choice schools. Our expert friends at US News &amp; World Report have
        agreed to give you a <b>free</b> consultation to help you get into your dream school.
      </Text>
      <NextButton>Claim my free consultation</NextButton>
    </Box>
  );
}

function QuestionStudentInfo() {
  const { setKey, userInfo, onClickNext } = useUserInfo();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm();

  const gradYearOptions = ["2024", "2025", "2026", "2027", "2028"].map((o) => ({
    label: o,
    value: o,
  }));
  const [firstName, setFirstName] = useState(userInfo.firstName);
  const [lastName, setLastName] = useState(userInfo.lastName);
  const [phoneNumber, setPhoneNumber] = useState(userInfo.phoneNumber);
  const [studentEmail, setStudentEmail] = useState("");
  const [gradYear, setGradYear] = useState(
    createValueList(userInfo.gradYear ? [userInfo.gradYear] : [], gradYearOptions)[0] || {
      label: "2023",
      value: "2023",
    },
  );
  const isParent = userInfo.consumerType === "parent";

  const onSubmit = () => {
    if (!hasTenDigits(phoneNumber)) {
      setError("phoneNumber", { message: "Please enter a valid phone number." });
      return;
    }
    if (studentEmail && !isValidEmail(studentEmail)) {
      setError("studentEmail", { message: "Please enter a valid email address." });
      return;
    }

    setKey("studentFirstName", firstName);
    setKey("studentLastName", lastName);
    setKey("studentPhone", phoneNumber);
    setKey("studentGradYear", gradYear.value);
    setKey("studentEmail", studentEmail.value);
    onClickNext();
  };

  return (
    <Question
      title={isParent ? "🧑‍🎓 Tell us about your student." : "🧑‍🎓 Tell us about yourself."}
      onClickNext={onSubmit}
    >
      <Box textAlign={"left"}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Text mb={2}>What is your {isParent && "child's "}name?</Text>
          <Flex direction={{ base: "column", md: "row" }} gap={4}>
            <FormControl isInvalid={!!errors.firstName}>
              <Input
                flex={1}
                placeholder="First Name"
                value={firstName}
                {...register("firstName", { required: "Please enter your first name." })}
                onChange={(e) => setFirstName(e.target.value)}
              />
              {errors.firstName && <FormErrorMessage>{errors.firstName.message}</FormErrorMessage>}
            </FormControl>
            <FormControl isInvalid={!!errors.lastName}>
              <Input
                flex={1}
                placeholder="Last Name"
                value={lastName}
                {...register("lastName", { required: "Please enter your last name." })}
                onChange={(e) => setLastName(e.target.value)}
              />
              {errors.lastName && <FormErrorMessage>{errors.lastName.message}</FormErrorMessage>}
            </FormControl>
          </Flex>
          <Flex direction={{ base: "column", md: "row" }} gap={4} mt={4}>
            <Box flex={1}>
              <Text mb={2}>What is your {isParent && "child's "}phone number?</Text>
              <FormControl isInvalid={!!errors.phoneNumber}>
                <Input
                  placeholder="(512) 715-3458"
                  value={phoneNumber}
                  {...register("phoneNumber", { required: "Please enter your phone number." })}
                  onChange={(e) => setPhoneNumber(e.target.value)}
                />
                {errors.phoneNumber && (
                  <FormErrorMessage>{errors.phoneNumber.message}</FormErrorMessage>
                )}
              </FormControl>
            </Box>
            <Box flex={1}>
              <Text mb={2}>What year will you{isParent && "r child"} graduate?</Text>
              <Select
                variant="flushed"
                placeholder="Select an option."
                value={gradYear}
                onChange={setGradYear}
                options={gradYearOptions}
              />
            </Box>
          </Flex>
          {isParent && (
            <>
              <Text mt={4}>What is your child&apos;s email address?</Text>
              <FormControl isInvalid={!!errors.studentEmail}>
                <Input
                  placeholder="ethanzhang@gmail.com"
                  value={studentEmail}
                  {...register("studentEmail", { required: "Please enter your email address." })}
                  onChange={(e) => setStudentEmail(e.target.value)}
                />
                {errors.studentEmail && (
                  <FormErrorMessage>{errors.studentEmail.message}</FormErrorMessage>
                )}
              </FormControl>
            </>
          )}
          <Flex w="100%">
            <Spacer />
            <Button
              type={"submit"}
              mt={4}
              variant="solid"
              colorScheme="red"
              rightIcon={<ChevronRightIcon />}
            >
              Next
            </Button>
          </Flex>
        </form>
      </Box>
    </Question>
  );
}

function SignIn() {
  const { userInfo, setKey, onClickNext } = useUserInfo();
  const { currentUser, refreshUserData } = useUserData();
  const api = useAPI();
  const [savingToProfile, setSavingToProfile] = useState(false);

  const onComplete = () => onClickNext(5);

  useEffect(() => {
    if (currentUser) {
      setSavingToProfile(true);
      onComplete();
    }
  }, [!!currentUser]);

  return (
    <Question title="Create an Account">
      {savingToProfile ? (
        <Text fontSize="2xl">Loading...</Text>
      ) : (
        <>
          <Text fontSize="xl">This will help us save your progress.</Text>
          <HSCreateAccount onComplete={onComplete} />
        </>
      )}
    </Question>
  );
}

function QuestionParentInfo() {
  const { setKey, userInfo, onClickNext } = useUserInfo();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm();

  const [firstName, setFirstName] = useState(userInfo.firstName);
  const [lastName, setLastName] = useState(userInfo.lastName);
  const [phoneNumber, setPhoneNumber] = useState(userInfo.phoneNumber);
  const [parentEmail, setParentEmail] = useState("");

  const onSubmit = () => {
    if (phoneNumber === userInfo.studentPhone) {
      setError("phoneNumber", {
        message: "Please enter a different phone number from the student phone number.",
      });
      return;
    }
    if (!hasTenDigits(phoneNumber)) {
      setError("phoneNumber", { message: "Please enter a valid phone number." });
      return;
    }
    if (parentEmail && !isValidEmail(parentEmail)) {
      setError("parentEmail", { message: "Please enter a valid email address." });
      return;
    }
    setKey("parentFirstName1", firstName);
    setKey("parentLastName1", lastName);
    setKey("parentPhone1", phoneNumber);
    setKey("parentEmail1", parentEmail);
    onClickNext();
  };

  const isStudent = userInfo.consumerType === "student";

  return (
    <Question title="👩‍👦 Parent Information">
      <Box textAlign={"left"}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Text mb={2}>What is your {isStudent && "parent's "}name?</Text>
          <Flex direction={{ base: "column", md: "row" }} gap={4}>
            <FormControl isInvalid={!!errors.firstName}>
              <Input
                flex={1}
                placeholder="First Name"
                value={firstName}
                {...register("firstName", { required: "Please enter your first name." })}
                onChange={(e) => setFirstName(e.target.value)}
              />
              {errors.firstName && <FormErrorMessage>{errors.firstName.message}</FormErrorMessage>}
            </FormControl>
            <FormControl isInvalid={!!errors.lastName}>
              <Input
                flex={1}
                placeholder="Last Name"
                value={lastName}
                {...register("lastName", { required: "Please enter your last name." })}
                onChange={(e) => setLastName(e.target.value)}
              />
              {errors.lastName && <FormErrorMessage>{errors.lastName.message}</FormErrorMessage>}
            </FormControl>
          </Flex>
          <Box flex={1} mt={4}>
            <Text mb={2}>What is your {isStudent && "parent's "}phone number?</Text>
            <FormControl isInvalid={!!errors.phoneNumber}>
              <Input
                placeholder="(512) 715-3458"
                value={phoneNumber}
                {...register("phoneNumber", { required: "Please enter your phone number." })}
                onChange={(e) => setPhoneNumber(e.target.value)}
              />
              {errors.phoneNumber && (
                <FormErrorMessage>{errors.phoneNumber.message}</FormErrorMessage>
              )}
            </FormControl>
          </Box>
          {isStudent && (
            <>
              <Text mt={4}>What is your parent&apos;s email address?</Text>
              <FormControl isInvalid={!!errors.parentEmail}>
                <Input
                  placeholder="ethanzhang@yahoo.com"
                  value={parentEmail}
                  {...register("parentEmail", { required: "Please enter your email address." })}
                  onChange={(e) => setParentEmail(e.target.value)}
                />
                {errors.parentEmail && (
                  <FormErrorMessage>{errors.parentEmail.message}</FormErrorMessage>
                )}
              </FormControl>
            </>
          )}
          <Text mt={4} fontSize={"xs"} color={"gray.700"} fontStyle={"italic"}>
            By submitting, you agree to receive personalized follow-up and marketing messages from
            CollegeAdvisor.com by email, phone, and automated text. Consent is not a condition of
            purchase. Standard rates apply.
          </Text>
          <Flex w="100%">
            <Spacer />
            <Button
              type={"submit"}
              mt={4}
              variant="solid"
              colorScheme="red"
              rightIcon={<ChevronRightIcon />}
            >
              Next
            </Button>
          </Flex>
        </form>
      </Box>
    </Question>
  );
}

function Finish() {
  const { currentUser } = useUserData();
  const { userInfo } = useUserInfo();
  const api = useAPI();

  const onSubmit = async () => {
    if (!!currentUser) {
      const emailKey = userInfo.consumerType === "parent" ? "parentEmail1" : "studentEmail";
      userInfo[emailKey] = currentUser.email;
    }
    userInfo.originSource = userInfo.consumerType === "parent" ? "Parent" : "Student";
    delete userInfo.consumerType;
    await api.post("users/collegeadvisor-lead", true, userInfo);
  };

  useEffect(() => {
    onSubmit();
  }, []);

  return (
    <Question title={"🎉 You're all set!"}>
      <Text mt={4} fontSize={"xl"}>
        We&apos;ll send you an email with a link to schedule your free consultation with
        CollegeAdvisor.
      </Text>
    </Question>
  );
}

function QuestionUserTypeWrapper() {
  const { userInfo, onClickNext } = useUserInfo();
  useEffect(() => {
    // if (userInfo.consumerType) {
    //   onClickNext()
    // }
  }, []);
  return <QuestionUserType skippable={false} />;
}

export default function CollegeAdvisorForm({ context = "page" }) {
  const components = [
    Intro,
    QuestionUserTypeWrapper,
    QuestionStudentInfo,
    QuestionParentInfo,
    SignIn,
    Finish,
  ];
  return (
    <CollegeAdvisorFormContext.Provider value={{ context }}>
      <UserInfoProvider pageName="collegeadvisor" nQuestions={components.length - 1}>
        <CurrentQuestionRenderer components={components} />
      </UserInfoProvider>
    </CollegeAdvisorFormContext.Provider>
  );
}
